import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useContext, useState } from 'react';
import MlxActionIcon from '../../../components/common/MlxActionIcon/MlxActionIcon';
import MlxInputSwitch from '../../../components/common/MlxInputSwitch/MlxInputSwitch';
import MlxBlockUI from '../../../components/common/MlxBlockUI/MlxBlockUI';
import { toTypeCase } from '../../../utils/common';
import { confirmPopup } from 'primereact/confirmpopup';
import { AuthCtx } from '../../../context/AuthCtxProvider';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { checkActionSubPermission } from '../../../utils/common';
import { PermissionKeys, SubPermissionKeys, AlertVariant } from '../../../constants/constants';
import MlxConfirmPopup from '../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { getAddonPlansList, _AddonPlanFromAPI, getAddonById, deleteAddonPlan, archiveAddonPlan } from '../../../helpers/subscriptionHelper';
import { ADDON_PLANS_LIST } from '../../../constants/queryKeys';
import { _ToastMessageProps } from '../../../constants/staticTypes';
import logger from '../../../utils/logger';
import './AddOnPlanList.scss';

type Props = {
    onEditPlan?: (plan: _AddonPlanFromAPI) => void;
    setToastMessage?: React.Dispatch<React.SetStateAction<_ToastMessageProps>>;
};

function AddOnPlanList({ onEditPlan, setToastMessage }: Props) {
    const [isFetchingAddonDetails, setIsFetchingAddonDetails] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { userType } = useContext(AuthCtx);
    const queryClient = useQueryClient();

    // Fetch addon plans from API
    const addonPlansQuery = useQuery({
        queryKey: [ADDON_PLANS_LIST, currentPage, rowsPerPage],
        queryFn: () => getAddonPlansList(queryClient, currentPage, rowsPerPage),
        onError: (error) => {
            logger.log('Failed to fetch addon plans', error);
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true, // Keep previous data while fetching new page
    });

    // Derive addon plans and pagination from query data
    const addOnPlans = (addonPlansQuery.data?.data?.data?.data as _AddonPlanFromAPI[]) || [];
    const paginationData = addonPlansQuery.data?.data?.data?.pagination;
    const totalRecords = paginationData?.total || 0;

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: (addonId: string) => deleteAddonPlan(queryClient, addonId),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries([ADDON_PLANS_LIST]);
            if (setToastMessage) {
                setToastMessage({
                    show: true,
                    message: response?.message || 'Addon deleted successfully',
                    variant: AlertVariant.SUCCESS,
                });
            }
        },
        onError: (error: any) => {
            logger.log('Failed to delete addon', error);
            if (setToastMessage) {
                setToastMessage({
                    show: true,
                    message: error?.response?.data?.message || 'Failed to delete addon. Please try again.',
                    variant: AlertVariant.ERROR,
                });
            }
        },
    });

    // Status change mutation
    const statusChangeMutation = useMutation({
        mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => archiveAddonPlan(queryClient, id, isActive),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries([ADDON_PLANS_LIST]);
            if (setToastMessage) {
                setToastMessage({
                    show: true,
                    message: response?.message || 'Addon status updated successfully',
                    variant: AlertVariant.SUCCESS,
                });
            }
        },
        onError: (error: any) => {
            logger.log('Failed to update addon status', error);
            if (setToastMessage) {
                setToastMessage({
                    show: true,
                    message: error?.response?.data?.message || 'Failed to update addon status. Please try again.',
                    variant: AlertVariant.ERROR,
                });
            }
        },
    });

    const handleStatusChange = (rowData: _AddonPlanFromAPI) => {
        statusChangeMutation.mutate({ id: rowData.id, isActive: !rowData.isActive });
    };

    const confirmStatusChange = (event: any, rowData: _AddonPlanFromAPI) => {
        confirmPopup({
            target: event.target,
            message: `Are you sure you want to ${rowData.isActive ? 'deactivate' : 'activate'} this add-on plan?`,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleStatusChange(rowData),
            reject: () => {},
        });
    };

    const planNameTemplate = (rowData: _AddonPlanFromAPI) => {
        return <div className="addon-plan-name">{rowData.name}</div>;
    };

    const priceTemplate = (rowData: _AddonPlanFromAPI) => {
        // Get the first active price from the prices array
        if (rowData.prices && rowData.prices.length > 0) {
            const activePrice = rowData.prices.find((price) => price.isActive) || rowData.prices[0];

            // Format the amount (convert from cents to dollars)
            const amount = parseFloat(activePrice.amount);
            const formattedAmount = `$${amount.toFixed(2)}`;

            // Add recurring interval if it's a recurring price
            if (activePrice.type === 'recurring' && activePrice.recurring?.interval) {
                const interval = activePrice.recurring.interval;
                return `${formattedAmount}/${interval}`;
            }

            // For one-time prices
            return formattedAmount;
        }
        return '-';
    };

    const statusTemplate = (rowData: _AddonPlanFromAPI) => {
        const handleStatusClick = (event: any) => {
            confirmStatusChange(event, rowData);
        };

        return (
            <div className="d-flex flex-row align-items-center justify-content-between" style={{ maxWidth: '110px', minWidth: '110px' }}>
                <span className="me-2">{toTypeCase(rowData.isActive ? 'Active' : 'Inactive', 'capitalize')}</span>
                <MlxInputSwitch name="addon-plan-status" checked={rowData.isActive} onClick={handleStatusClick} />
            </div>
        );
    };

    const handleEdit = async (rowData: _AddonPlanFromAPI) => {
        if (onEditPlan) {
            setIsFetchingAddonDetails(true);
            try {
                // Fetch full addon details including prices and features
                const response = await getAddonById(queryClient, rowData.id);
                const fullAddonData = response?.data?.data as _AddonPlanFromAPI;
                onEditPlan(fullAddonData || rowData);
            } catch (error) {
                logger.log('Failed to fetch addon details', error);
                // Fallback to basic data if fetch fails
                onEditPlan(rowData);
            } finally {
                setIsFetchingAddonDetails(false);
            }
        }
    };

    const handleDelete = (rowData: _AddonPlanFromAPI) => {
        deleteMutation.mutate(rowData.id);
    };

    const confirmDelete = (event: any, rowData: _AddonPlanFromAPI) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you sure you want to delete this add-on plan?',
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleDelete(rowData),
            reject: () => {},
        });
    };

    const onPageChange = (event: any) => {
        setCurrentPage(event.page + 1); // PrimeReact uses 0-based page index
        setRowsPerPage(event.rows);
    };

    const actionTemplate = (rowData: _AddonPlanFromAPI) => {
        return (
            <div className="d-flex flex-row align-items-center justify-content-end" style={{ minWidth: '70px' }}>
                {userType === 'superAdmin' ||
                checkActionSubPermission(queryClient, PermissionKeys.MANAGE_STAFF, SubPermissionKeys.EDIT_STAFF) ? (
                    <MlxActionIcon className="me-3 edit-icon-selection" actionType="EDIT" onClick={() => handleEdit(rowData)} />
                ) : (
                    <></>
                )}
                {userType === 'superAdmin' ||
                checkActionSubPermission(queryClient, PermissionKeys.MANAGE_STAFF, SubPermissionKeys.DELETE_STAFF) ? (
                    <MlxActionIcon
                        className="me-3 delete-icon-selection"
                        actionType="DELETE"
                        onClick={(event) => confirmDelete(event, rowData)}
                    />
                ) : (
                    <></>
                )}
            </div>
        );
    };

    return (
        <>
            <MlxConfirmPopup />
            <MlxBlockUI
                isLoading={
                    addonPlansQuery.isFetching || isFetchingAddonDetails || deleteMutation.isLoading || statusChangeMutation.isLoading
                }
                opacity={0.5}
            >
                <DataTable
                    className="custom-p-table addon-plan-table"
                    value={addOnPlans}
                    paginator
                    lazy
                    first={(currentPage - 1) * rowsPerPage}
                    rows={rowsPerPage}
                    totalRecords={totalRecords}
                    onPage={onPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    size="small"
                    removableSort
                    scrollable
                    scrollHeight="flex"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                    dataKey="id"
                >
                    <Column
                        style={{ maxWidth: '18rem', minWidth: '18rem' }}
                        header="Add-On Plan"
                        field="name"
                        sortable
                        body={planNameTemplate}
                    ></Column>
                    <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} field="price" header="Price" body={priceTemplate}></Column>
                    <Column style={{ minWidth: '110px' }} header="Status" body={statusTemplate}></Column>
                    <Column header="" style={{ minWidth: '70px' }} body={actionTemplate}></Column>
                </DataTable>
            </MlxBlockUI>
        </>
    );
}

export default AddOnPlanList;
